import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { enumAttributeConverter } from '../enum-types';
import { getGlobalStyleSheets } from '../../global-styles';

import * as SidePanelTags from './side-panel.tagnames';

import '../icon';
import '../placeholders';

import {
    AtlSidePanelProps,
    SidePanelSizes,
    SidePanelPlacements,
} from './side-panel.props';
import { AtlantisEvent } from '../helpers';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
    SidePanelCloseBlockedEvent,
    SidePanelClosedEvent,
    SidePanelCloseEvent,
    SidePanelOpenedEvent,
    SidePanelOpenEvent,
} from './side-panel.events';

const SidePanelSizesValues = {
    [SidePanelSizes.Small]: 'panel-small',
    [SidePanelSizes.Medium]: 'panel-medium',
    [SidePanelSizes.Large]: 'panel-large',
    [SidePanelSizes.Larger]: 'panel-larger',
};

const SizePanelPlacementValues = {
    [SidePanelPlacements.Right]: 'offcanvas-end',
    [SidePanelPlacements.Left]: 'offcanvas-start',
    [SidePanelPlacements.Top]: 'offcanvas-top',
    [SidePanelPlacements.Bottom]: 'offcanvas-bottom',
};

type OffCanvasType = {
    show: () => void;
    toggle: () => void;
    hide: () => void;
    dispose: () => void;
    _focustrap: {
        deactivate: () => void;
    };
};

@customElement(SidePanelTags.SidePanel)
export class AtlSidePanel extends LitElement implements AtlSidePanelProps {
    declare show: boolean;
    declare resizable?: boolean;
    declare size?: SidePanelSizes;
    declare placement?: SidePanelPlacements;
    declare backdrop?: boolean;
    declare bodyScrolling?: boolean;
    declare static?: boolean;
    declare keyboard?: boolean;
    declare displayCloseButton?: boolean;

    declare label?: string;

    private _sidePanelOffcanvas?: OffCanvasType;

    @query('.side-panel')
    private _sidePanel?: HTMLElement;

    @query('.resizable-indicator')
    private _resizeIndicator?: HTMLElement;

    private _isResizing: boolean = false;

    static properties = {
        show: { type: Boolean },
        resizable: { type: Boolean },
        size: { type: String, converter: enumAttributeConverter(SidePanelSizes) },
        placement: {
            type: String,
            converter: enumAttributeConverter(SidePanelPlacements),
        },
        backdrop: { type: Boolean },
        bodyScrolling: { attribute: 'body-scrolling', type: Boolean },
        static: { type: Boolean },
        keyboard: { type: Boolean },
        displayCloseButton: { attribute: 'display-close-button', type: Boolean },
        label: { type: String },
        hidePanelCallback: { type: Function },
    };

    static styles = [
        ...getGlobalStyleSheets(),
        css`
         :host {
            display: block;
         }

         .side-panel {
            overflow: hidden;
         }

         .offcanvas-header {
            display: block;
         }

         .side-panel-content {
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
            flex:1;
            min-height: 0;
         }

         .side-panel-action-buttons {
            flex-shrink: 0;
         }

         .offcanvas-header {
            flex-shrink: 0;
         }

         .offcanvas-body.side-panel-body {
            flex: 1;
            overflow: auto;
            min-height: 0;
         }

         ::slotted([slot='footer']) {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.25rem;
            padding: 1rem;
            flex-shrink: 0;
         }

         ::slotted([slot='side-panel-stripe']) {
            display: flex;
            gap: 0.5rem;
            flex-direction: column;
            align-items: center;
            padding: 1rem 0.25rem 0 0.25rem;
            border-right: 1px solid var(--tti-sidepanel-stripe-border-color);
            flex-shrink: 0;
         }
      `,
    ];

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this._sidePanel?.addEventListener(
            'hidePrevented.bs.offcanvas',
            this.dispatchCloseBlockedEvent
        );
        this._sidePanel?.addEventListener(
            'hidden.bs.offcanvas',
            this.dispatchClosedEvent
        );
        this._sidePanel?.addEventListener(
            'hide.bs.offcanvas',
            this.dispatchCloseEvent
        );
        this._sidePanel?.addEventListener(
            'show.bs.offcanvas',
            this.dispatchOpenEvent
        );
        this._sidePanel?.addEventListener(
            'shown.bs.offcanvas',
            this.dispatchOpenedEvent
        );

        this._sidePanelOffcanvas = new bootstrap.Offcanvas(this._sidePanel);
    }

    override connectedCallback() {
        super.connectedCallback();
        this._onMouseMove = this._onMouseMove.bind(this);
        this._onMouseUp = this._onMouseUp.bind(this);
    }

    override disconnectedCallback() {
        this._sidePanel?.removeEventListener(
            'hide.bs.offcanvas',
            this.dispatchCloseEvent
        );
        this._sidePanel?.removeEventListener(
            'hidden.bs.offcanvas',
            this.dispatchClosedEvent
        );
        this._sidePanel?.removeEventListener(
            'hidePrevented.bs.offcanvas',
            this.dispatchCloseBlockedEvent
        );
        this._sidePanel?.removeEventListener(
            'show.bs.offcanvas',
            this.dispatchOpenEvent
        );
        this._sidePanel?.removeEventListener(
            'shown.bs.offcanvas',
            this.dispatchOpenedEvent
        );

        if (this.resizable) {
            window.removeEventListener('mousemove', this._onMouseMove);
            window.removeEventListener('mouseup', this._onMouseUp);
            document.body.style.userSelect = '';

            if (this._isResizing) {
                document.body.style.cursor = '';
            }
        }

        this._sidePanelOffcanvas?.hide();

        super.disconnectedCallback();
    }


    protected override updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);

        if (changedProperties.has('size') || changedProperties.has('placement') || changedProperties.has('resizable')) {
            this._sidePanelOffcanvas?.dispose();
            this._sidePanelOffcanvas = new bootstrap.Offcanvas(this._sidePanel);
            this._sidePanel!.style.cssText = '';


            if (this.show) {
                this._sidePanelOffcanvas?.show();
            }
        }

        if (changedProperties.has('show')) {
            this._sidePanel!.style.cssText = '';
            if (this._sidePanelOffcanvas && this.show) {
                this._sidePanelOffcanvas.show();
            } else if (this._sidePanelOffcanvas) {
                this._sidePanelOffcanvas.hide();
            }
        }
    }

    private dispatchCloseEvent = () =>
        this.dispatchEvent(new SidePanelCloseEvent());
    private dispatchClosedEvent = () =>
        this.dispatchEvent(new SidePanelClosedEvent());
    private dispatchOpenEvent = () =>
        this.dispatchEvent(new SidePanelOpenEvent());
    private dispatchOpenedEvent = () => {
        this._sidePanelOffcanvas?._focustrap.deactivate();
        this.dispatchEvent(new SidePanelOpenedEvent());
    };
    private dispatchCloseBlockedEvent = () =>
        this.dispatchEvent(new SidePanelCloseBlockedEvent(false));

    private _handleHidePanel = () => {
        if (this.static) {
            this.dispatchEvent(new SidePanelCloseBlockedEvent(true));
            return;
        }
        this._sidePanelOffcanvas?.hide();
    };

    private _onMouseDown() {
        this._isResizing = true;
        window.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('mouseup', this._onMouseUp);
        document.body.style.userSelect = 'none'; // don't highlight text while resizing
        this._resizeIndicator?.classList.add('highlight');
    }

    private _onMouseMove(e: MouseEvent) {
        if (!this._isResizing || !this._sidePanel) return;

        switch (this.placement ?? SidePanelPlacements.Right) {
            case SidePanelPlacements.Left: {
                const edge = this._sidePanel.getBoundingClientRect().left;
                const newWidth = e.clientX - edge;
                this._horizontalMove(newWidth);
                break;
            }
            case SidePanelPlacements.Right: {
                const edge = this._sidePanel.getBoundingClientRect().right;
                const newWidth = edge - e.clientX;
                this._horizontalMove(newWidth);
                break;
            }
            case SidePanelPlacements.Top: {
                const edge = this._sidePanel.getBoundingClientRect().top;
                const newHeight = e.clientY - edge;
                this._verticalMove(newHeight);
                break;
            }
            case SidePanelPlacements.Bottom: {
                const edge = this._sidePanel.getBoundingClientRect().bottom;
                const newHeight = edge - e.clientY;
                this._verticalMove(newHeight);
                break;
            }
            default:
                break;
        }
    }

    private _horizontalMove(newWidthPx: number) {
        if (!this._sidePanel) return;
        this._sidePanel.style.width = newWidthPx + 'px';
        document.body.style.cursor = 'ew-resize';
        // set max width here as if we overwrite the initial offset max width the initial size is lost
        this._sidePanel.style.maxWidth = '80vw';
    }

    private _verticalMove(newHeightPx: number) {
        if (!this._sidePanel) return;
        this._sidePanel!.style.height = newHeightPx + 'px';
        document.body.style.cursor = 'ns-resize';
        // set max height here as if we overwrite the initial offset max height the initial size is lost
        this._sidePanel.style.maxHeight = '80vh';
    }

    private _onMouseUp() {
        this._isResizing = false;
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup', this._onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = ''; // allow text selection again
        this._resizeIndicator?.classList.remove('highlight');
    }

    onSlotChange(e: AtlantisEvent<Event, HTMLSlotElement>) {
        const assignedNodes = e.target.assignedNodes({
            flatten: true,
        }) as HTMLElement[];

        const storedButtonElements = this.getAllButtonsElements(assignedNodes);

        storedButtonElements.forEach((button) => {
            button.setAttribute('class', 'w-100');
            button.setAttribute('fullWidth', 'true');
        });
    }

    getAllButtonsElements = (nodes: HTMLElement[]): HTMLElement[] => {
        let buttonElements: HTMLElement[] = [];
        nodes.forEach((node) => {
            if (node.nodeName.includes('ATLANTIS-BUTTON')) {
                buttonElements.push(node);
            } else if (node instanceof HTMLElement) {
                buttonElements = buttonElements.concat(
                    this.getAllButtonsElements(
                        Array.from(node.children) as HTMLElement[]
                    )
                );
            }
        });
        return buttonElements;
    };

    renderCloseButton() {
        if (this.displayCloseButton) {
            return html`<button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click=${this._handleHidePanel}
         ></button>`;
        }

        return nothing;
    }

    render() {
        const sidePanelClasses = [
            'side-panel',
            'offcanvas',
            SidePanelSizesValues[this.size ?? SidePanelSizes.Medium],
            SizePanelPlacementValues[this.placement ?? SidePanelPlacements.Right],
            this.resizable ? 'resizable' : '',
        ];

        if (!this.backdrop) {
            sidePanelClasses.push('backdrop-opacity-0');
        }

        return html`
         <div
            part="panel"
            class=${sidePanelClasses.join(' ')}
            data-bs-scroll=${this.bodyScrolling ? 'true' : nothing}
            data-bs-backdrop=${this.static
                ? 'static'
                : this.backdrop
                    ? 'true'
                    : 'false'}
            data-bs-keyboard=${this.keyboard ? 'true' : 'false'}
            aria-label=${ifDefined(this.label)}
            tabindex="-1"
            role="dialog"
         >
            ${this.resizable ? html`<div class="resizable-handle" @mousedown=${this._onMouseDown}>
                <div class="resizable-indicator"></div>
                </div>` : nothing}
            <div class="d-flex h-100 overflow-hidden">
                <slot name="side-panel-stripe"></slot>
                <div class="side-panel-content" part="content">
                   <div class="side-panel-action-buttons" part="action-buttons">
                      <div>
                         <slot name="action-buttons"></slot>
                      </div>
                      ${this.renderCloseButton()}
                   </div>
                   <div class="offcanvas-header" part="header">
                      <slot name="header"></slot>
                   </div>
                   <div class="offcanvas-body side-panel-body component-custom-vertical-scrollbar" part="body">
                      <slot name="body"></slot>
                   </div>
                   <slot name="footer" @slotchange=${this.onSlotChange}></slot>
                </div>
            </div>
         </div>
      `;
    }
}
